import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { csrf, login as apiLogin, verify2FA, logout as apiLogout, me } from '@features/auth/api';
import {http} from '@services/http'

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTwoFactorRequired, setIsTwoFactorRequired] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // /api/auth/status returns { authenticated, user } with 200 in
        // both states. No 401 to deal with on public routes.
        const { data } = await me();
        setUser(data?.authenticated ? data.user : null);
      } catch {
        // Network-level failures only — endpoint never returns 401.
        setUser(null);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const signIn = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null); setLoading(true);
    try {
      // Get CSRF token first
      await csrf();
      
      // Small delay to ensure cookie is set
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const response = await apiLogin(email, password);
      const data = response.data;

      if (data?.two_factor_required) {
        setIsTwoFactorRequired(true);
        return;
      }
      
      // Use user data returned from login instead of making separate API call
      if (data?.user) {
        //console.log('Login successful, user data:', data.user);
        setUser(data.user);
        navigate('/user-home', { replace: true });
      } else {
        // Fallback to /auth/status if login didn't echo user data.
        // Login just succeeded, so we expect authenticated=true here —
        // if not, something's badly wrong on the backend.
        const res = await me();
        if (!res.data?.authenticated) {
          throw new Error('Session not established after login.');
        }
        setUser(res.data.user);
        navigate('/user-home', { replace: true });
      }
    } catch (err) {
      // console.log('Login error:', err);
      // console.log('Error response:', err.response);
      // console.log('Error response data:', err.response?.data);
      // console.log('Error status:', err.response?.status);
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitTwoFactor = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null); setLoading(true);
    try {
      const response = await verify2FA(twoFactorCode);
      const data = response.data;
      
      // Use user data returned from 2FA verification instead of making separate API call
      if (data?.user) {
        //console.log('2FA verification successful, user data:', data.user);
        setUser(data.user);
        navigate('/user-home', { replace: true });
      } else {
        // Fallback to /auth/status if 2FA didn't echo user data.
        const res = await me();
        if (!res.data?.authenticated) {
          throw new Error('Session not established after 2FA.');
        }
        setUser(res.data.user);
        navigate('/user-home', { replace: true });
      }
    } catch (err) {
      // console.log('2FA verification error:', err);
      // console.log('2FA error response:', err.response);
      setError(err.response?.data?.message || 'Failed to verify two-factor authentication code.');
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await apiLogout();
      setUser(null);
      navigate('/login', { replace: true });
    } catch {
      setError('Logout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      // state
      email, setEmail, password, setPassword, twoFactorCode, setTwoFactorCode,
      user, checking, error, loading, isTwoFactorRequired, setError,
      // actions
      signIn, submitTwoFactor, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
